import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Snackbar,
  alpha,
  TablePagination,
  Grid,
  Stack,
  Tooltip,
  Switch,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoIcon from '@mui/icons-material/Photo';
import CloseIcon from '@mui/icons-material/Close';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import API, { uploadImage } from '../../api/axios';
import { getImageUrl, FALLBACK_IMAGE } from '../../utils/imageUtils';
import useCategories from '../../hooks/useCategories';

// Predefined colors for known categories; unknown ones get a fallback
const CATEGORY_COLOR_MAP = {
  Domestic: { color: '#3b82f6', bg: alpha('#3b82f6', 0.1) },
  International: { color: '#10b981', bg: alpha('#10b981', 0.1) },
  Religious: { color: '#f59e0b', bg: alpha('#f59e0b', 0.1) },
  Wildlife: { color: '#22c55e', bg: alpha('#22c55e', 0.1) },
};

const getCategoryColor = (cat) =>
  CATEGORY_COLOR_MAP[cat] || { color: '#8b5cf6', bg: alpha('#8b5cf6', 0.1) };

const ManageTours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    category: 'Domestic',
    title: '',
    description: '',
    duration: '',
    price: '',
    imageUrl: '',
    imagePreview: null,
    highlights: [''],
    isBestSelling: false,
  });

  const [formErrors, setFormErrors] = useState({});

  // Dynamic categories from API
  const { categories, loading: catLoading } = useCategories();
  const defaultCategory = categories[0] || 'Domestic';

  useEffect(() => { fetchTours(); }, []);

  const fetchTours = async () => {
    try {
      const { data } = await API.get('/tours?limit=1000');
      setTours(data.tours || data || []);
    } catch {
      setError('Failed to load tours');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (tour = null) => {
    if (tour) {
      setEditingTour(tour);
      setFormData({
        category: tour.category,
        title: tour.title,
        description: tour.description,
        duration: tour.duration,
        price: tour.price.toString(),
        imageUrl: tour.imageUrl,
        imagePreview: tour.imageUrl,
        highlights: tour.highlights?.length ? tour.highlights : [''],
        isBestSelling: tour.isBestSelling || false,
      });
    } else {
      setEditingTour(null);
      setFormData({ category: defaultCategory, title: '', description: '', duration: '', price: '', imageUrl: '', imagePreview: null, highlights: [''], isBestSelling: false });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTour(null);
    setUploading(false);
    setFormErrors({});
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [e.target.name]: value }));
  };
  const handleHighlightChange = (i, val) => setFormData((prev) => ({ ...prev, highlights: prev.highlights.map((h, idx) => (idx === i ? val : h)) }));
  const addHighlight = () => setFormData((prev) => ({ ...prev, highlights: [...prev.highlights, ''] }));
  const removeHighlight = (i) => setFormData((prev) => ({ ...prev, highlights: prev.highlights.filter((_, idx) => idx !== i) }));

  const validate = () => {
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.imageUrl) errors.imageUrl = 'Image is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const tourData = { ...formData, price: formData.price ? parseFloat(formData.price) : 0, highlights: formData.highlights.filter((h) => h.trim()) };
      if (editingTour) {
        await API.put(`/tours/${editingTour._id}`, tourData);
        setSnackbar({ open: true, message: 'Tour updated successfully', severity: 'success' });
      } else {
        await API.post('/tours', tourData);
        setSnackbar({ open: true, message: 'Tour created successfully', severity: 'success' });
      }
      fetchTours();
      handleCloseDialog();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save tour. Please try again.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setSnackbar({ open: true, message: 'Invalid file type. Only JPEG, PNG, WebP, and GIF are allowed.', severity: 'error' });
      return;
    }

    const maxImageSize = parseInt(process.env.REACT_APP_MAX_IMAGE_SIZE, 10);
    if (!maxImageSize) {
      setSnackbar({ open: true, message: 'Image size limit is not configured properly in .env', severity: 'error' });
      return;
    }
    if (file.size > maxImageSize) {
      setSnackbar({ open: true, message: `File too large. Maximum size is ${maxImageSize / 1024 / 1024}MB.`, severity: 'error' });
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({ ...prev, imagePreview: previewUrl }));
    setUploading(true);

    try {
      const result = await uploadImage(file, 'tours');
      if (result.success && result.path) {
        setFormData((prev) => ({ ...prev, imageUrl: result.path }));
        setSnackbar({ open: true, message: 'Image uploaded successfully', severity: 'success' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to upload image', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setFormData((prev) => ({ ...prev, imageUrl: '', imagePreview: null }));
  };

  const openDeleteDialog = (id) => { setDeleteId(id); setDeleteDialogOpen(true); };
  const closeDeleteDialog = () => { setDeleteId(null); setDeleteDialogOpen(false); };

  const handleDelete = async () => {
    try {
      await API.delete(`/tours/${deleteId}`);
      setSnackbar({ open: true, message: 'Tour deleted successfully', severity: 'success' });
      fetchTours();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete tour', severity: 'error' });
    } finally {
      closeDeleteDialog();
    }
  };

  const filtered = tours.filter((t) => !search || t.title?.toLowerCase().includes(search.toLowerCase()) || t.category?.toLowerCase().includes(search.toLowerCase()));
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, color: 'text.primary' }}>
            Manage Tours
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Add, edit or remove your tour packages
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            borderRadius: 2.5,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 15px rgba(102,126,234,0.35)',
            '&:hover': { boxShadow: '0 8px 25px rgba(102,126,234,0.5)' }
          }}
        >
          Add New Tour
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Search and Table */}
      <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <TextField
            size="small"
            placeholder="Search tours by title or category..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ maxWidth: 400, width: '100%' }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} /></InputAdornment>,
              sx: { borderRadius: 2, bgcolor: '#f8f9fc' }
            }}
          />
        </Box>

        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fc' }}>
                {['Tour Details', 'Category', 'Duration', 'Price (INR)', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', py: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>No tours found matching your search.</TableCell></TableRow>
              ) : paginated.map((tour) => {
                const catColors = getCategoryColor(tour.category);
                return (
                  <TableRow key={tour._id} sx={{ '&:hover': { bgcolor: alpha('#667eea', 0.02) }, transition: 'background-color 0.2s' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          component="img"
                          src={getImageUrl(tour.imageUrl) || FALLBACK_IMAGE}
                          alt={tour.title}
                          sx={{ width: 64, height: 48, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0, bgcolor: 'grey.100', border: '1px solid rgba(0,0,0,0.05)' }}
                          onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_IMAGE; }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 250 }}>{tour.title}</Typography>
                          {tour.isBestSelling && (
                            <Chip label="Best Seller" size="small" sx={{ mt: 0.5, height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#f59e0b', color: 'white', width: 'fit-content' }} />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={tour.category} size="small" sx={{ fontWeight: 600, color: catColors.color, bgcolor: catColors.bg, border: `1px solid ${alpha(catColors.color, 0.2)}`, borderRadius: 1.5 }} />
                    </TableCell>
                    <TableCell><Typography variant="body2" fontWeight={500} color="text.secondary">{tour.duration}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={700}>₹{tour.price?.toLocaleString('en-IN')}</Typography></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Tour">
                          <IconButton size="small" onClick={() => handleOpenDialog(tour)} sx={{ color: '#3b82f6', bgcolor: alpha('#3b82f6', 0.1), '&:hover': { bgcolor: alpha('#3b82f6', 0.2) } }}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Tour">
                          <IconButton size="small" onClick={() => openDeleteDialog(tour._id)} sx={{ color: '#ef4444', bgcolor: alpha('#ef4444', 0.1), '&:hover': { bgcolor: alpha('#ef4444', 0.2) } }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10]}
          sx={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 800, pb: 2 }}>
          {editingTour ? 'Edit Tour Package' : 'Create New Tour'}
          <IconButton onClick={handleCloseDialog} size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: alpha('#ef4444', 0.1) } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderBottom: 'none', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Stack spacing={3} sx={{ pt: 1 }}>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Category" name="category" value={formData.category} onChange={handleChange}>
                  {catLoading ? <MenuItem value={formData.category}>{formData.category}</MenuItem> : categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth label="Tour Title *" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Majestic Shimla & Manali" error={!!formErrors.title} helperText={formErrors.title} />
              </Grid>
            </Grid>

            <TextField fullWidth multiline rows={4} label="Description" name="description" value={formData.description} onChange={handleChange} placeholder="Describe the tour experience..." />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Duration" name="duration" value={formData.duration} onChange={handleChange} placeholder="e.g. 5 Days / 4 Nights" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Price (INR)" name="price" type="number" value={formData.price} onChange={handleChange} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
              </Grid>
            </Grid>

            <FormGroup sx={{ bgcolor: alpha('#f59e0b', 0.05), p: 2, borderRadius: 2, border: `1px solid ${alpha('#f59e0b', 0.2)}` }}>
              <FormControlLabel
                control={<Switch checked={formData.isBestSelling} onChange={handleChange} name="isBestSelling" color="warning" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={700} color="text.primary">Mark as Best Selling Package</Typography>
                    <Typography variant="caption" color="text.secondary">This will feature the tour prominently on the home page.</Typography>
                  </Box>
                }
              />
            </FormGroup>

            {/* Image Upload Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>Tour Cover Image *</Typography>
              <Box
                component="label"
                sx={{
                  display: 'block',
                  border: '2px dashed',
                  borderColor: formData.imagePreview ? 'success.main' : 'grey.300',
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                  cursor: uploading ? 'default' : 'pointer',
                  bgcolor: formData.imagePreview ? alpha('#10b981', 0.02) : '#fafafa',
                  transition: 'all 0.2s ease',
                  pointerEvents: uploading ? 'none' : 'auto',
                  '&:hover': { borderColor: 'primary.main', bgcolor: alpha('#667eea', 0.02) },
                }}
              >
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }} onChange={handleImageChange} disabled={uploading} />

                {formData.imagePreview ? (
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={getImageUrl(formData.imagePreview)}
                      alt="Preview"
                      style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 12, objectFit: 'cover', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                      onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_IMAGE; }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => { e.preventDefault(); clearImage(); }}
                      sx={{
                        position: 'absolute', top: -12, right: -12, bgcolor: 'background.paper', color: 'error.main', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        '&:hover': { bgcolor: 'error.main', color: 'white' },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 2, borderRadius: '50%', bgcolor: alpha('#667eea', 0.1), color: '#667eea' }}>
                      <CloudUploadIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                      Click to upload or drag and drop
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      SVG, PNG, JPG or GIF (max. {parseInt(process.env.REACT_APP_MAX_IMAGE_SIZE, 10) / 1024 / 1024}MB)
                    </Typography>
                  </Box>
                )}
              </Box>
              {uploading && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'center' }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" fontWeight={500} color="primary.main">Uploading your image...</Typography>
                </Box>
              )}
            </Box>

            {/* Highlights Section */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>Tour Highlights</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addHighlight} sx={{ textTransform: 'none', fontWeight: 600 }}>Add More</Button>
              </Box>

              <Stack spacing={1.5}>
                {formData.highlights.map((h, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={`E.g. Visit to Taj Mahal (Highlight ${i + 1})`}
                      value={h}
                      onChange={(e) => handleHighlightChange(i, e.target.value)}
                      sx={{ bgcolor: '#fafafa', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <IconButton
                      color="error"
                      onClick={() => removeHighlight(i)}
                      disabled={formData.highlights.length === 1}
                      sx={{ bgcolor: alpha('#ef4444', 0.05), '&:hover': { bgcolor: alpha('#ef4444', 0.15) } }}
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>

          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 3, gap: 1.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, color: 'text.secondary', borderColor: 'grey.300' }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving || uploading} sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', boxShadow: '0 4px 12px rgba(102,126,234,0.3)', '&:hover': { boxShadow: '0 6px 18px rgba(102,126,234,0.45)' } }}>
            {saving ? <CircularProgress size={24} sx={{ color: 'white' }} /> : (editingTour ? 'Update Tour' : 'Save Tour')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} PaperProps={{ sx: { borderRadius: 3, maxWidth: 400, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#ef4444' }}>Delete Tour?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            Are you sure you want to permanently delete this tour? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={closeDeleteDialog} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)' }}>
            Yes, Delete it
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert variant="filled" severity={snackbar.severity} sx={{ borderRadius: 2, fontWeight: 500, boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageTours;